import { useEffect, useState } from 'react';
import { db } from '../db/client';
import { claimInvites } from '../db/auth';
import { useActiveOrgId } from '../shared/orgState';
import { PickerOverlay } from '../widget-overlay/PickerOverlay';
import { CommentComposer, type ComposerTarget } from '../widget-overlay/CommentComposer';
import { PinLayer } from '../widget-overlay/PinLayer';
import { Toolbar } from './Toolbar';
import { SidePanel } from './SidePanel';

interface AppProps {
  onUnlock: () => void;
  onLock: () => void;
}

const FIRST_RUN_KEY = 'uinoter.firstRunDismissed';

export function App({ onUnlock, onLock }: AppProps) {
  const { isLoading, user, error } = db.useAuth();
  const [activeOrgId, setActiveOrgId] = useActiveOrgId();
  const [picking, setPicking] = useState(false);
  const [composerTarget, setComposerTarget] = useState<ComposerTarget | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [focusCommentId, setFocusCommentId] = useState<string | null>(() => {
    const u = new URL(window.location.href);
    return u.searchParams.get('uinoter_focus');
  });

  const [hintVisible, setHintVisible] = useState<boolean>(() => {
    try { return localStorage.getItem(FIRST_RUN_KEY) !== '1'; } catch { return false; }
  });
  useEffect(() => {
    if (!hintVisible) return;
    const t = setTimeout(() => {
      setHintVisible(false);
      try { localStorage.setItem(FIRST_RUN_KEY, '1'); } catch { /* ignore */ }
    }, 3500);
    return () => clearTimeout(t);
  }, [hintVisible]);

  // Once a real session is observed, the user has actively unlocked the widget.
  useEffect(() => {
    if (user) onUnlock();
  }, [user, onUnlock]);

  // Auto-claim invites whenever a new user lands.
  useEffect(() => {
    if (!user?.id || !user.email) return;
    claimInvites(user.id, user.email).catch(() => {});
  }, [user?.id, user?.email]);

  // Auto-open the panel on first mount if signed-out, so the user knows
  // where to log in.
  useEffect(() => {
    if (!isLoading && !user) setPanelOpen(true);
  }, [isLoading, user]);

  const ready = !!user && !!activeOrgId;

  return (
    <>
      {ready && picking && (
        <PickerOverlay
          onPick={(target) => {
            setComposerTarget(target);
            setPicking(false);
          }}
          onCancel={() => setPicking(false)}
        />
      )}

      {composerTarget && user && activeOrgId && (
        <CommentComposer
          target={composerTarget}
          orgId={activeOrgId}
          userId={user.id}
          userEmail={user.email!}
          onClose={() => setComposerTarget(null)}
        />
      )}

      {ready && (
        <PinLayer
          orgId={activeOrgId!}
          currentUserId={user!.id}
          currentUserEmail={user!.email!}
          focusCommentId={focusCommentId}
          onFocused={() => setFocusCommentId(null)}
        />
      )}

      <Toolbar
        signedIn={!!user}
        ready={ready}
        picking={picking}
        onTogglePicker={() => setPicking((p) => !p)}
        onOpenPanel={() => setPanelOpen(true)}
        onHide={() => {
          onLock();
          window.dispatchEvent(new CustomEvent('uinoter:hide'));
        }}
      />

      {hintVisible && (
        <div className="uin-firstrun-hint" role="status">
          UI Noter is on. Press <b>⌘⇧E</b> or click <b>×</b> to hide.
        </div>
      )}

      {panelOpen && (
        <SidePanel
          authLoading={isLoading}
          authError={error?.message ?? null}
          user={user ?? null}
          activeOrgId={activeOrgId}
          onPickOrg={setActiveOrgId}
          onClose={() => setPanelOpen(false)}
        />
      )}
    </>
  );
}
